import { useState, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { map, omit, compact, isEmpty } from "lodash"
import { List, ListItemButton, ListItemText } from "@mui/material"
import { CategoryShape } from "./helpers/prop-types"
import ExpandIcon from "./expandIcon"
import MetricsSubcategory from "./metrics-subcategory"

export default function MetricsCategory({ category, disableOptions, selectedMetrics, setSelectedMetrics, includeMetricTypes, metricsSearchValue }) {
  const [openSections, setOpenSections] = useState({})
  const [collapsedSearchSections, setCollapsedSearchSections] = useState({})

  useEffect(() => {
    if (metricsSearchValue === "" && !isEmpty(collapsedSearchSections)) {
      setCollapsedSearchSections({})
    }
  }, [metricsSearchValue, collapsedSearchSections])

  const toggleSection = useCallback((category, metric, metricType) => {
    const key = compact([category, metric, metricType]).join(".")
    // If we are using the search, then found items are auto-expanded,
    // so we need store the open/collapsed state of search results in a separate object
    if (metricsSearchValue) {
      setCollapsedSearchSections({
        ...collapsedSearchSections,
        [key]: !collapsedSearchSections[key],
      })
    } else {
      setOpenSections({
        ...openSections,
        [key]: !openSections[key],
      })
    }
  }, [setOpenSections, openSections, collapsedSearchSections, metricsSearchValue])

  const getIsSectionOpen = useCallback((category, metric, metricType) => {
    const key = compact([category, metric, metricType]).join(".")
    if (metricsSearchValue) {
      return !collapsedSearchSections?.[key]
    }
    return !!openSections?.[key]
  }, [openSections, collapsedSearchSections, metricsSearchValue])

  const {
    shouldShowCategory,
    subcategoriesFound,
    subcategoriesAndItemsSelected,
    totalSubcategoriesAndItems,
    subcategories,
  } = category

  const label = category.label || category.category
  const isSectionOpen = getIsSectionOpen(label)
  const subcategoryCountLabel = metricsSearchValue
    ? `(${subcategoriesFound} found)`
    : `(${subcategoriesAndItemsSelected} / ${totalSubcategoriesAndItems})`

  function toggleSelectedMetricColumn(category, metric, metricType) {
    const key = compact([category, metric, metricType]).join(".")
    if (selectedMetrics[key]) {
      setSelectedMetrics(omit(selectedMetrics, key))
    } else if (!disableOptions) {
      setSelectedMetrics({ ...selectedMetrics, [key]: true })
    }
  }

  function handleClick() {
    const handler = subcategoriesFound ? toggleSection : toggleSelectedMetricColumn
    handler(label)
  }

  if (!shouldShowCategory) {
    return null
  }

  return <List disablePadding className="metrics-category">
    <ListItemButton
      classes={{ root: "list-subcategory list-item" }}
      onClick={handleClick}
    >
      <ListItemText>
        {label} <span className="items-count">{subcategoryCountLabel}</span>
      </ListItemText>
      <ExpandIcon
        isOpen={isSectionOpen}
        isHidden={!subcategoriesFound}
      />
    </ListItemButton>

    {subcategories && isSectionOpen && <List className="searchable-list" disablePadding>
      {map(subcategories, subcategory => <MetricsSubcategory
        key={`${category.type}.${subcategory.id}`}
        categoryType={category.type}
        subcategory={subcategory}
        toggleSection={toggleSection}
        toggleSelectedMetricColumn={toggleSelectedMetricColumn}
        disableOptions={disableOptions}
        openSections={openSections}
        selectedMetrics={selectedMetrics}
        includeMetricTypes={includeMetricTypes}
        metricsSearchValue={metricsSearchValue}
        getIsSectionOpen={getIsSectionOpen}
      />)}
    </List>}
  </List>
}

MetricsCategory.propTypes = {
  category: CategoryShape.isRequired,
  disableOptions: PropTypes.bool.isRequired,
  selectedMetrics: PropTypes.object.isRequired,
  setSelectedMetrics: PropTypes.func.isRequired,
  includeMetricTypes: PropTypes.bool,
  metricsSearchValue: PropTypes.string.isRequired,
}
